import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Typography, Box, Paper, Grid, TextField,
  MenuItem, FormControlLabel, Checkbox, Button,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PeopleIcon from '@mui/icons-material/People';
import { Operator } from '../types';
import { PasswordField } from './PasswordField';

interface OperatorFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (operator: Operator) => void;
  selectedOperator?: Operator;
  isEditMode?: boolean;
}

const commonTextFieldStyles = {
  mb: 2,
  backgroundColor: 'white',
  borderRadius: 1
};

const AddNewLocationPopUp: React.FC<OperatorFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  selectedOperator,
  isEditMode
}) => {
  const theme = useTheme();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [newPassword, setNewPassword] = useState("");
  const [form, setForm] = useState<Operator>({
    id: 0,
    email: '',
    userName: '',
    password: '',
    phone: 0,
    isActive: true,
    roleId: 0,
    firstName: '',
    lastName: '',
    gender: '',
    dateOfBirth: new Date(),
    isDeleted: false,
    employeeId: ''
  });

  useEffect(() => {
  setErrors({});
  if (selectedOperator) {
    setForm({
      ...selectedOperator,
      password: "", // do NOT load actual password
    });

    setNewPassword("");
  } else {
    setForm({
      id: 0,
      email: '',
      userName: '',
      password: '',
      phone: 0,
      isActive: true,
      roleId: 0,
      firstName: '',
      lastName: '',
      gender: '',
      dateOfBirth: new Date(),
      isDeleted: false,
      employeeId: ''
    });

    setNewPassword("");
  }
}, [selectedOperator, open]);

  const handleChange = (field: keyof Operator, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
  if (!validateForm()) return;

  const payload = {
    ...form,
    password: isEditMode
              ? (newPassword.trim() ? newPassword : selectedOperator?.password ?? "")
              : newPassword
  };

  onSubmit(payload);
};


  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!form.firstName.trim()) newErrors.firstName = "First name is required";
    if (!form.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!form.userName.trim()) newErrors.userName = "User name is required";

    if (!isEditMode && !newPassword.trim()) {
        newErrors.password = "Password is required";
      }


    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      newErrors.email = "Enter a valid email address";
    }

    // Phone validation (10 digits)
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(String(form.phone))) {
      newErrors.phone = "Phone must be 10 digits (e.g., 1234567890)";
    }

    // Employee Id validation (Alpha numeric)
    const employeeIdRegex = /^[A-Za-z0-9]+$/;
    if (!employeeIdRegex.test(String(form.employeeId))) {
      newErrors.employeeId = "Employee ID must contain only letters and numbers";
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
    >
      <DialogTitle sx={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.primary }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            <PeopleIcon /> {isEditMode ? "Edit Operator" : "Add New Operator"}
          </Typography>
          <IconButton onClick={onClose} sx={{ color: theme.palette.text.primary }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: theme.palette.background.paper }}>
        {Object.keys(errors).length > 0 && (
            <Box
              sx={{
                backgroundColor: "#fee2e2",
                border: "1px solid #ef4444",
                color: "#b91c1c",
                p: 2,
                borderRadius: 1,
                mb: 2
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                Please fix the following errors:
              </Typography>

              {Object.values(errors).map((err, idx) => (
                <Typography key={idx} variant="body2">
                  • {err}
                </Typography>
              ))}
            </Box>
          )}
        <Paper elevation={3} sx={{ p: 2, backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
          <Grid container spacing={3}>

            {/* First + Last Name */}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={form.firstName}
                    onChange={e => handleChange("firstName", e.target.value)}
                    sx={commonTextFieldStyles}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={form.lastName}
                    onChange={e => handleChange("lastName", e.target.value)}
                    sx={commonTextFieldStyles}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* DOB, Gender, Employee ID */}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date of Birth"
                    value={ form.dateOfBirth ?? '' }
                    onChange={e => handleChange("dateOfBirth", e.target.value)}
                    sx={theme => ({
                        ...commonTextFieldStyles,
                        "& input::-webkit-calendar-picker-indicator": {
                          filter:
                            theme.palette.mode === "dark"
                              ? "invert(80%) sepia(80%) saturate(200%) hue-rotate(180deg)"
                              : "invert(40%) sepia(80%) saturate(200%) hue-rotate(180deg)"
                        }
                      })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={3}>
                  <TextField
                    select
                    fullWidth
                    label="Gender"
                    value={form.gender}
                    onChange={e => handleChange("gender", e.target.value)}
                    sx={commonTextFieldStyles}
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Others">Others</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={5}>
                  <TextField
                    fullWidth
                    label="Employee ID"
                    value={form.employeeId}
                    onChange={e => handleChange("employeeId", e.target.value)}
                    sx={commonTextFieldStyles}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Email + Phone */}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={form.email}
                    onChange={e => handleChange("email", e.target.value)}
                    sx={commonTextFieldStyles}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={form.phone}
                    onChange={e => handleChange("phone", Number(e.target.value))}
                    sx={commonTextFieldStyles}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Username + Password */}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="User Name"
                    value={form.userName}
                    onChange={e => handleChange("userName", e.target.value)}
                    sx={commonTextFieldStyles}
                  />
                </Grid>

                <Grid item xs={6}>

                  <PasswordField
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      sx={commonTextFieldStyles}
                    />
                </Grid>
              </Grid>
            </Grid>

            {/* Active Checkbox */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.isActive}
                    onChange={e => handleChange("isActive", e.target.checked)}
                  />
                }
                label="Active Operator"
                sx={{ mb: 2 }}
              />
            </Grid>

          </Grid>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ backgroundColor: theme.palette.background.default }}>
        <Button
          variant="contained"
          sx={{ background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' }, }}
          onClick={handleSubmit}
        >
          {isEditMode ? "Update Operator" : "Create Operator"}
        </Button>

        <Button
          variant="contained"
          sx={{ background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' }, }}
          onClick={onClose}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddNewLocationPopUp;